import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '@/app/routes.js';
import { BrandLightbulb } from '@/components/icons/BrandLightbulb.js';
import { useCatalog } from '@/lib/i18n/catalog-context.js';
import { cn } from '@/lib/utils.js';

/**
 * Логотип нового макета: лампочка + «MathMind».
 *
 * ⚠️ Это НЕ замена `BrandWordmark`, а второй объект, и жить им предстоит рядом до
 * волны 9. Старый воспроизводит логотип прода — `<h1 class="h4">` с RFS-шкалой
 * (22.9px при окне 836px), вес 500; новый — 18px на десктопе, 17px на мобильном,
 * вес 700, зазор 9px. Пока часть страниц не переверстана, они выглядят по-разному,
 * и подменять один другим значит менять вид ещё не тронутых экранов.
 *
 * ⚠️ `<span>`, а не `<h1>`. В проде логотип был заголовком первого уровня на каждой
 * странице, из-за чего настоящий заголовок экрана оказывался вторым, а у скринридера
 * все страницы назывались «MathMind». Заголовок теперь принадлежит содержимому.
 */
interface BrandMarkProps {
  className?: string;
  /** Крупный логотип: подвал лендинга (20px) и тёмная панель экрана входа (26px). */
  size?: 'header' | 'lg' | 'xl';
  /** Логотип как ссылка на главную. Выключается там, где мы уже на главной. */
  asLink?: boolean;
}

const SIZE: Record<NonNullable<BrandMarkProps['size']>, string> = {
  header: 'gap-2 text-[17px] md:gap-[9px] md:text-[18px]',
  lg: 'gap-[9px] text-[20px]',
  xl: 'gap-[10px] text-[26px]',
};

const BULB_SIZE: Record<NonNullable<BrandMarkProps['size']>, string> = {
  header: 'h-[1.05em] w-[0.78em]',
  lg: 'h-[1.05em] w-[0.78em]',
  xl: 'h-[1em] w-[0.75em]',
};

export function BrandMark({
  className,
  size = 'header',
  asLink = true,
}: BrandMarkProps): ReactElement {
  const c = useCatalog();

  const content = (
    <span
      className={cn('inline-flex items-center font-bold whitespace-nowrap', SIZE[size], className)}
      data-brand-mark
    >
      <BrandLightbulb className={cn('text-mm-accent', BULB_SIZE[size])} />
      {c.landing.brand.name}
    </span>
  );

  if (!asLink) return content;

  return (
    <Link to={ROUTES.home} className="no-underline">
      {content}
    </Link>
  );
}
